import { useSyncExternalStore } from 'react'
import { TAB_ID } from '~/lib/constants'
import { mountActiveMapRender } from '~/lib/mapRender/active'
import { mountBlack } from '~/lib/outputMapping/black'
import { selectOutputInput, watchInput } from '~/lib/outputMapping/input'
import { selectOutputBlack } from '~/store/outputMapping/selectors'
import store from '~/store'
import { mirrorClosed, mirrorOpened, showErrorDialog } from '~/store/app/thunks'
import { pushError } from '~/store/ui'

const logger = console.scope('Mirror')

const DOC = `<!doctype html>
<html><head><meta charset="utf-8"><title>Oylo — Mirror</title>
<style>
	html,body{margin:0;padding:0;height:100%;background:#000;overflow:hidden}
	body{position:relative}
	video{display:block;width:100vw;height:100dvh;object-fit:contain;background:#000}
</style></head>
<body><video id="mirror_video" autoplay muted playsinline></video></body></html>`

const POLL_MS = 1000

let win = null
let video = null
let detach = null
let pollId = null
let targetScreen = null
let opening = null
// Bumped by every open and by close, so an open still awaiting the screen gives up when closed first.
let launch = 0
const listeners = new Set()

const emit = () => listeners.forEach(cb => cb())

function attach(stream) {
	if (!video || !win || win.closed || video.srcObject === stream) return
	video.srcObject = stream
	// Required: the element was created programmatically into a document that was never navigated, so the `autoplay` attribute alone does not reliably start a MediaStream. `muted` satisfies the autoplay policy.
	if (stream) video.play().catch(e => logger.warn('play() refused', e))
}

// Follows the mirror's input as it is switched from the main window.
function watchMirrorInput() {
	let input = null
	let unwatch = null
	const sync = () => {
		const next = selectOutputInput(store.getState(), 'mirror')
		if (next === input) return
		input = next
		unwatch?.()
		unwatch = watchInput(input, attach)
	}
	sync()
	const unsubscribe = store.subscribe(sync)
	return () => {
		unsubscribe()
		unwatch?.()
	}
}

function writeDoc(w) {
	const d = w.document
	d.open()
	d.write(DOC)
	d.close()
	return d.getElementById('mirror_video')
}

const goFullscreen = d => d.documentElement.requestFullscreen(targetScreen ? { screen: targetScreen } : undefined)

// The open spends the click, so this succeeds only where Chrome's AutomaticFullscreenAllowedForUrls policy lists the app; elsewhere a click or F in the window enters.
const autoFullscreen = d => goFullscreen(d).catch(e => logger.info('no automatic fullscreen', e))

/**
 * Fullscreen is one-way: click, `F` and `F11` all enter, and only Escape (which the browser handles itself) leaves. A projection surface should never drop out of fullscreen because someone clicked the image.
 */
function bindChrome(w, d) {
	const enter = () => {
		if (d.fullscreenElement) return
		goFullscreen(d).catch(e => logger.warn('fullscreen refused', e))
	}
	const onKey = e => {
		if (e.code !== 'KeyF' && e.code !== 'F11') return
		// F11 is the browser's own fullscreen, which leaves the page's chrome in place; preventing it lets the Fullscreen API give the same result as the other two triggers.
		e.preventDefault()
		enter()
	}
	d.addEventListener('keydown', onKey)
	d.addEventListener('click', enter)
	d.addEventListener('dblclick', enter)
	return () => {
		d.removeEventListener('keydown', onKey)
		d.removeEventListener('click', enter)
		d.removeEventListener('dblclick', enter)
	}
}

function bootstrap(w) {
	let el = null
	try {
		el = writeDoc(w)
	} catch (e) {
		logger.warn('immediate write failed, deferring to load', e)
	}
	if (el) return finish(w, el)
	// The initial `about:blank` document was not ready. Retry once the child settles.
	const retry = () => {
		if (!win || win.closed) return
		finish(w, writeDoc(w))
	}
	if (w.document?.readyState === 'complete') setTimeout(retry, 0)
	else w.addEventListener('load', retry, { once: true })
}

function finish(w, el) {
	// A named window can come back already bound; its old document's mounts would outlive it.
	detach?.()
	video = el
	const unbindChrome = bindChrome(w, w.document)
	autoFullscreen(w.document)
	// The child runs no script of its own; the parent drives its mapping from this store.
	const unmap = mountActiveMapRender({ video: el, box: w.document.body, store, win: w })
	const unblack = mountBlack({ doc: w.document, store, read: s => selectOutputBlack(s, 'mirror') })
	const unwatch = watchMirrorInput()
	const onChildHide = () => handleChildGone()
	w.addEventListener('pagehide', onChildHide)
	w.addEventListener('resize', emit)
	detach = () => {
		unmap()
		unblack()
		unbindChrome()
		unwatch()
		w.removeEventListener('pagehide', onChildHide)
		w.removeEventListener('resize', emit)
	}
	emit()
}

function stopPoll() {
	if (pollId) clearInterval(pollId)
	pollId = null
}

// `pagehide` does not fire on every OS window-close path, so polling `win.closed` is the only dependable signal. 1s is imperceptible for a button state flip.
function startPoll() {
	stopPoll()
	pollId = setInterval(() => {
		if (!win || win.closed) handleChildGone()
	}, POLL_MS)
}

function handleChildGone() {
	if (!win) return
	stopPoll()
	detach?.()
	detach = null
	video = null
	win = null
	targetScreen = null
	emit()
	store.dispatch(mirrorClosed())
}

// Chromium-only: 'granted', 'prompt', or null — no second screen, no API, or denied — where the mirror is a plain window the user drags over and fullscreens with F.
async function screenAccess() {
	if (!window.getScreenDetails || !window.screen?.isExtended) return null
	try {
		const { state } = await navigator.permissions.query({ name: 'window-management' })
		return state === 'denied' ? null : state
	} catch {
		// An older Chromium names the permission otherwise; asking still works.
		return 'prompt'
	}
}

async function pickScreen() {
	try {
		const details = await window.getScreenDetails()
		return details.screens.find(s => !s.isPrimary) || null
	} catch (e) {
		logger.info('window-management unavailable', e)
		return null
	}
}

const geometry = s =>
	s
		? { left: s.availLeft, top: s.availTop, width: s.availWidth, height: s.availHeight }
		: { left: 100, top: 100, width: 1280, height: 720 }

// Asked once the mirror is open, from this window brought forward so the prompt is not under the mirror; on a grant the mirror moves over.
function placeOnceAllowed(id) {
	window.focus()
	pickScreen().then(s => {
		if (!s || id !== launch || !win || win.closed) return
		targetScreen = s
		const g = geometry(s)
		// Fullscreen on the first screen is moved over by fullscreen on this one.
		if (win.document.fullscreenElement) return autoFullscreen(win.document)
		win.moveTo(g.left, g.top)
		win.resizeTo(g.width, g.height)
		autoFullscreen(win.document)
	})
}

/** Opens the mirror, or focuses the open one. A press while an open is under way gets that open's promise: a second would open the same named window and bind it twice. */
export function openMirror() {
	if (win && !win.closed) {
		win.focus()
		return Promise.resolve(true)
	}
	opening ??= launchMirror().finally(() => {
		opening = null
	})
	return opening
}

async function launchMirror() {
	const id = ++launch
	const access = await screenAccess()
	// Only a granted permission is awaited before opening. A first-time prompt can outlast the click's activation (5s), and the open is then blocked.
	targetScreen = access === 'granted' ? await pickScreen() : null
	if (id !== launch) return false
	const g = geometry(targetScreen)
	// ⚠️ Geometry alone opens a window; `popup=true` opens a popup that cannot fullscreen, and no features at all opens a tab.
	win = window.open('', `oylo_mirror_${TAB_ID}`, `left=${g.left},top=${g.top},width=${g.width},height=${g.height}`)
	if (!win) {
		logger.warn('window.open returned null — popup blocked')
		store.dispatch(
			pushError({
				code: 'mirror_blocked',
				title: 'Mirror blocked',
				message: 'Your browser blocked the mirror window. Allow pop-ups for this site and try again.',
			})
		)
		store.dispatch(showErrorDialog())
		return false
	}
	bootstrap(win)
	startPoll()
	emit()
	store.dispatch(mirrorOpened())
	if (access === 'prompt') placeOnceAllowed(id)
	return true
}

export function closeMirror() {
	launch++
	if (win && !win.closed) win.close()
	handleChildGone()
}

export const isMirrorOpen = () => !!win && !win.closed

export function subscribeMirror(cb) {
	listeners.add(cb)
	return () => listeners.delete(cb)
}

export const useMirrorOpen = () => useSyncExternalStore(subscribeMirror, isMirrorOpen, isMirrorOpen)

// Its pin is normalised to its window, so an editor of it needs the window's shape. 0 when closed.
const mirrorAspect = () => (isMirrorOpen() && win.innerHeight ? win.innerWidth / win.innerHeight : 0)

export const useMirrorAspect = () => useSyncExternalStore(subscribeMirror, mirrorAspect, mirrorAspect)

// An orphaned child would hold a dead stream. `pagehide` rather than `beforeunload`: registering a `beforeunload` disables bfcache for this long-lived tab, a cost paid by every user who never opens a mirror.
if (typeof window !== 'undefined') {
	window.addEventListener('pagehide', () => {
		if (win && !win.closed) win.close()
	})
}
